import type { BillingModel } from './index';
import { billingReducer, createInitialState } from './reducer';
import type { BillingAction, BillingState } from './reducer';
import { computed, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import { MutationObserver } from '@tanstack/query-core';
import type {
    BillingHistoryEntry,
    ChangeSubscriptionRequest,
    Subscription,
} from '@thinkalpha/access-ws-client/contracts/subscription.js';
import type { SecurityPackage } from '@thinkalpha/access-ws-client/contracts/user.js';
import type { ProTraderStatus } from '@thinkalpha/common/schemas/auth.js';
import { isAxiosError } from 'axios';
import { isEqual } from 'es-toolkit';
import { inject, injectable } from 'src/features/ioc';
import { getProTraderStatusFromToken } from 'src/features/oauth2';
import { connectReducerDevtools } from 'src/lib/devtools';
import type { ReducerDevtoolsConnection } from 'src/lib/devtools';
import { showInviteCodesForProfessionalTraders } from 'src/lib/featureFlags/flags/showInviteCodesForProfessionalTraders';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import { SubscriptionPlanCatalogModel } from 'src/models/SubscriptionPlanCatalogModel';
import { statusToIsProfessionalTrader } from 'src/models/SubscriptionPlanCatalogModel/rules';
import {
    getBillingHistoryQuery,
    getCancelSubscriptionMutation,
    getChangeSubscriptionMutation,
    getMySubscriptionsQuery,
    getReactivateSubscriptionMutation,
    getSetDefaultPaymentMethodMutation,
    getSetupIntentMutation,
    invalidateMySubscriptionsQuery,
} from 'src/queries/subscriptions';
import { getUserSecurityPackageQuery } from 'src/queries/user';
import type { SessionService } from 'src/services/SessionService';
import type { ReactBindings } from 'src/types/bindings';
import type { z } from 'zod';

@injectable()
export class BillingModelImpl implements BillingModel {
    readonly #disposableStack = new DisposableStack();

    #state = signal<BillingState>(createInitialState());

    #subscriptionsQuery: QueryObserverModel<Subscription[], Error, Subscription[]>;
    #billingHistoryQuery: QueryObserverModel<BillingHistoryEntry[], Error, BillingHistoryEntry[]>;
    #securityPackageQuery: QueryObserverModel<z.infer<typeof SecurityPackage>, Error, z.infer<typeof SecurityPackage>>;
    #planCatalog!: SubscriptionPlanCatalogModel;

    #cancelSubscriptionMutation: MutationObserver<Subscription, Error, { id: string; immediately?: boolean }>;
    #reactivateSubscriptionMutation: MutationObserver<Subscription, Error, string>;
    #changeSubscriptionMutation: MutationObserver<
        Subscription,
        Error,
        { id: string; request: ChangeSubscriptionRequest }
    >;
    #setupIntentMutation: MutationObserver<{ clientSecret: string }, Error, void>;
    #setDefaultPaymentMethodMutation: MutationObserver<{ success: boolean }, Error, string>;

    #devtools: ReducerDevtoolsConnection<BillingState, BillingAction> | undefined;
    #showInviteCodesForProfessionalTraders: ReadonlySignal<boolean> = signal(false);

    #dispatch(action: BillingAction): void {
        this.#state.value = billingReducer(this.#state.value, action);
        this.#devtools?.send(action, this.#state.peek());
    }

    readonly subscriptions: ReadonlySignal<z.infer<typeof Subscription>[]> = computed(
        () => this.#subscriptionsQuery?.signal.value.data ?? [],
    );

    readonly billingHistory: ReadonlySignal<z.infer<typeof BillingHistoryEntry>[]> = computed(
        () => this.#billingHistoryQuery?.signal.value.data ?? [],
    );

    readonly proTraderStatus: ReadonlySignal<ProTraderStatus> = computed(() =>
        getProTraderStatusFromToken(
            this.store.signal.value.auth?.masqAccessToken?.token ?? this.sessionService.accessToken.value,
        ),
    );

    readonly isProfessionalTrader: ReadonlySignal<boolean> = computed(() =>
        statusToIsProfessionalTrader(this.proTraderStatus.value),
    );

    readonly showInviteCodeEntry: ReadonlySignal<boolean> = computed(
        () => !this.isProfessionalTrader.value || this.#showInviteCodesForProfessionalTraders.value,
    );

    readonly plans: BillingModel['plans'] = computed(() => this.#planCatalog?.plans.value ?? []);
    readonly availablePlans: BillingModel['availablePlans'] = computed(
        () => this.#planCatalog?.availablePlans.value.filter((plan) => plan.pricing.length > 0) ?? [],
    );
    readonly inviteCode: BillingModel['inviteCode'] = computed(() => this.#planCatalog?.inviteCode.value ?? '');
    readonly inviteCodePlan: BillingModel['inviteCodePlan'] = computed(
        () => this.#planCatalog?.inviteCodePlan.value ?? null,
    );
    readonly plansLoading: BillingModel['plansLoading'] = computed(
        () => this.#planCatalog?.plansLoading.value ?? false,
    );
    readonly isInviteCodeValid: BillingModel['isInviteCodeValid'] = computed(
        () => this.#planCatalog?.isInviteCodeValid.value ?? null,
    );

    readonly loading: ReadonlySignal<boolean> = computed(
        () =>
            this.#subscriptionsQuery?.signal.value.isFetching ||
            this.#billingHistoryQuery?.signal.value.isFetching ||
            this.#planCatalog?.plansLoading.value ||
            false,
    );

    readonly error: ReadonlySignal<string | null> = computed(() => this.#state.value.error);

    readonly setupClientSecret: ReadonlySignal<string | null> = computed(
        () => this.#state.value.setupIntentResult?.data?.clientSecret ?? null,
    );

    readonly isUpdatingPaymentMethod: ReadonlySignal<boolean> = computed(
        () =>
            (this.#state.value.setupIntentResult?.isPending ?? false) ||
            (this.#state.value.setDefaultPaymentMethodResult?.isPending ?? false) ||
            false,
    );

    constructor(
        @inject('QueryObserverModelFactory')
        private queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        @inject('QueryClient')
        private queryClient: ReactBindings['QueryClient'],
        @inject('CurrentUserModel') private currentUserModel: ReactBindings['CurrentUserModel'],
        @inject('Store') private store: ReactBindings['Store'],
        @inject('FeatureFlagService') private featureFlagService: ReactBindings['FeatureFlagService'],
        @inject('SessionService') private sessionService: SessionService,
    ) {
        this.#showInviteCodesForProfessionalTraders = this.featureFlagService.getSignal(
            showInviteCodesForProfessionalTraders,
        );
        // Initialize queries
        this.#subscriptionsQuery = this.queryObserverModelFactory<Subscription[], Error, Subscription[]>(
            getMySubscriptionsQuery(),
        );
        this.#disposableStack.use(this.#subscriptionsQuery);

        this.#billingHistoryQuery = this.queryObserverModelFactory<BillingHistoryEntry[], Error, BillingHistoryEntry[]>(
            computed(() => getBillingHistoryQuery(this.#state.value.billingHistoryLimit)),
        );
        this.#disposableStack.use(this.#billingHistoryQuery);

        this.#planCatalog = new SubscriptionPlanCatalogModel({
            queryObserverModelFactory: this.queryObserverModelFactory,
            isProfessionalTrader: this.isProfessionalTrader,
            showInviteCodeEntry: this.showInviteCodeEntry,
        });
        this.#disposableStack.use(this.#planCatalog);

        // Initialize mutations
        this.#cancelSubscriptionMutation = new MutationObserver(this.queryClient, getCancelSubscriptionMutation());

        this.#reactivateSubscriptionMutation = new MutationObserver(
            this.queryClient,
            getReactivateSubscriptionMutation(),
        );

        this.#changeSubscriptionMutation = new MutationObserver(this.queryClient, getChangeSubscriptionMutation());

        this.#setupIntentMutation = new MutationObserver(this.queryClient, getSetupIntentMutation());
        this.#disposableStack.defer(
            this.#setupIntentMutation.subscribe((result) => {
                this.#dispatch({ type: 'SETUP_INTENT_RESULT_UPDATED', result });
            }),
        );

        this.#setDefaultPaymentMethodMutation = new MutationObserver(
            this.queryClient,
            getSetDefaultPaymentMethodMutation(),
        );
        this.#disposableStack.defer(
            this.#setDefaultPaymentMethodMutation.subscribe((result) => {
                this.#dispatch({ type: 'SET_DEFAULT_PAYMENT_METHOD_RESULT_UPDATED', result });
            }),
        );

        this.#securityPackageQuery = this.queryObserverModelFactory<
            z.infer<typeof SecurityPackage>,
            Error,
            z.infer<typeof SecurityPackage>
        >(getUserSecurityPackageQuery('me'));
        this.#disposableStack.use(this.#securityPackageQuery);
        let lastSubscriptions: z.infer<typeof Subscription>[] | undefined;
        this.#disposableStack.use(
            this.#securityPackageQuery.subscribe((x) => {
                if (!x.data) return;
                if (isEqual(x.data.subscriptions, lastSubscriptions)) return;
                lastSubscriptions = x.data.subscriptions;
                invalidateMySubscriptionsQuery(this.queryClient);
            }),
        );

        this.#devtools = connectReducerDevtools({ name: 'Billing' });
        if (this.#devtools) {
            this.#disposableStack.use(this.#devtools);
            this.#devtools.init(this.#state.value);
        }
    }

    async refreshSubscriptions(): Promise<void> {
        this.#dispatch({ type: 'CLEAR_ERROR' });
        const result = await this.#subscriptionsQuery.queryObserver.refetch();
        if (result.isError) {
            this.#dispatch({
                type: 'SET_ERROR',
                error: result.error instanceof Error ? result.error.message : 'Failed to load subscriptions',
            });
        }
    }

    async cancelSubscription(id: string, immediately?: boolean): Promise<void> {
        this.#dispatch({ type: 'CLEAR_ERROR' });
        try {
            await this.#cancelSubscriptionMutation.mutate({ id, immediately });
            this.currentUserModel.notifyEntitlementsChanged();
        } catch (e) {
            this.#dispatch({
                type: 'SET_ERROR',
                error: e instanceof Error ? e.message : 'Failed to cancel subscription',
            });
            throw e;
        }
    }

    async reactivateSubscription(id: string): Promise<void> {
        this.#dispatch({ type: 'CLEAR_ERROR' });
        try {
            await this.#reactivateSubscriptionMutation.mutate(id);
            this.currentUserModel.notifyEntitlementsChanged();
        } catch (e) {
            this.#dispatch({
                type: 'SET_ERROR',
                error: e instanceof Error ? e.message : 'Failed to reactivate subscription',
            });
            throw e;
        }
    }

    async changeSubscription(id: string, newPlanId: string, immediate?: boolean): Promise<void> {
        this.#dispatch({ type: 'CLEAR_ERROR' });
        try {
            const request = immediate ? { immediateNewPlanId: newPlanId } : { scheduledNewPlanId: newPlanId };
            await this.#changeSubscriptionMutation.mutate({ id, request });
            this.currentUserModel.notifyEntitlementsChanged();
        } catch (e) {
            let serverMessage: string | undefined;
            if (isAxiosError(e)) {
                const responseData = e.response?.data;
                if (typeof responseData?.message === 'string' && responseData.message.trim().length > 0) {
                    serverMessage = responseData.message;
                } else if (typeof responseData?.error === 'string' && responseData.error.trim().length > 0) {
                    serverMessage = responseData.error;
                }
            }
            this.#dispatch({
                type: 'SET_ERROR',
                error: serverMessage ?? (e instanceof Error ? e.message : 'Failed to change subscription'),
            });
            throw e;
        }
    }

    async startPaymentMethodUpdate(): Promise<void> {
        this.#dispatch({ type: 'CLEAR_ERROR' });
        try {
            await this.#setupIntentMutation.mutate();
        } catch (e) {
            this.#dispatch({
                type: 'SET_ERROR',
                error: e instanceof Error ? e.message : 'Failed to start payment method update',
            });
            throw e;
        }
    }

    async completePaymentMethodUpdate(paymentMethodId: string): Promise<void> {
        this.#dispatch({ type: 'CLEAR_ERROR' });
        try {
            await this.#setDefaultPaymentMethodMutation.mutate(paymentMethodId);
            this.#setupIntentMutation.reset();
            this.#dispatch({ type: 'RESET_SETUP_INTENT' });
        } catch (e) {
            this.#dispatch({
                type: 'SET_ERROR',
                error: e instanceof Error ? e.message : 'Failed to update payment method',
            });
            throw e;
        }
    }

    async fetchBillingHistory(limit?: number): Promise<void> {
        this.#dispatch({ type: 'CLEAR_ERROR' });
        this.#dispatch({ type: 'SET_BILLING_HISTORY_LIMIT', limit });
        const result = await this.#billingHistoryQuery.queryObserver.refetch();
        if (result.isError) {
            this.#dispatch({
                type: 'SET_ERROR',
                error: result.error instanceof Error ? result.error.message : 'Failed to load billing history',
            });
        }
    }

    async fetchPlans(): Promise<void> {
        this.#dispatch({ type: 'CLEAR_ERROR' });
        const inviteCode = this.showInviteCodeEntry.value ? this.inviteCode.value.trim() : '';
        const result = await this.#planCatalog.fetchPlans();
        if (result.isError) {
            const err = result.error;
            const status = isAxiosError(err) ? err.response?.status : undefined;
            this.#dispatch({
                type: 'SET_ERROR',
                error:
                    inviteCode && status === 404
                        ? 'Invalid invite code. Please check and try again.'
                        : err instanceof Error
                          ? err.message
                          : 'Failed to load plans',
            });
        }
    }

    setInviteCode(inviteCode: string): void {
        this.#planCatalog.setInviteCode(inviteCode);
    }

    resetInviteCode(): void {
        this.#planCatalog.resetInviteCode();
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
